/**
 * Rideshare terminology helper.
 *
 * A single source of truth for what to call the counterparty on a
 * rideshare trip, keyed by `rideshare_type`:
 *
 *   ride     → "driver"   (passenger transport by car)
 *   motor    → "rider"    (passenger on a motorbike-taxi)
 *   delivery → "courier"  (goods carrier)
 *
 * The `terr()` helper returns lowercase strings so the caller can
 * capitalise as needed. `titleTerm()` returns the Title-cased version
 * for headings ("Driver accepted your trip", etc.).
 *
 * Also exports `vehiclePinSvg(type, {color, size})` which produces the
 * inner SVG markup for a Leaflet divIcon — a car, motorbike, or
 * delivery-parcel glyph — so map pins reflect the trip type instead of
 * a generic car every time.
 */

export function term(rideshareType) {
  switch (rideshareType) {
    case "motor": return "motor rider";
    case "delivery": return "courier";
    case "runner": return "runner";
    case "ride":
    default: return "driver";
  }
}

export function titleTerm(rideshareType) {
  const t = term(rideshareType);
  // Title-case each word so "motor rider" -> "Motor Rider".
  return t.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/**
 * Nomenclature helpers for the top-level "unit of work" being tracked.
 * Runners run **jobs**, couriers run **deliveries**, drivers run
 * **trips**. Keeps the customer + driver copy natural regardless of
 * rideshare type. Two variants for grammar convenience:
 *   - `tripNoun("runner")` → "job"        (lowercase, singular)
 *   - `tripNounTitle("runner")` → "Job"   (Title Case)
 */
export function tripNoun(rideshareType) {
  switch (rideshareType) {
    case "runner": return "job";
    case "delivery": return "delivery";
    case "ride":
    case "motor":
    default: return "trip";
  }
}

export function tripNounPlural(rideshareType) {
  const n = tripNoun(rideshareType);
  return n === "delivery" ? "deliveries" : `${n}s`;
}

export function tripNounTitle(rideshareType) {
  const n = tripNoun(rideshareType);
  return n.charAt(0).toUpperCase() + n.slice(1);
}

/**
 * Return the inner SVG (path/shapes only, no <svg> wrapper) for the
 * requested rideshare type. Callers wrap it in their own `<svg>` element
 * so they can control size, stroke, and viewBox.
 */
export function vehicleGlyph(rideshareType, { stroke = "white", strokeWidth = 2 } = {}) {
  switch (rideshareType) {
    case "motor":
      // Motorbike — two wheels + handlebar. Lucide `bike` glyph.
      return `
        <circle cx="18.5" cy="17.5" r="3.5"/>
        <circle cx="5.5" cy="17.5" r="3.5"/>
        <circle cx="15" cy="5" r="1"/>
        <path d="M12 17.5V14l-3-3 4-3 2 3h2"/>
      `;
    case "delivery":
      // Delivery van — box body + wheels. Lucide `truck` glyph.
      return `
        <path d="M10 17h4V5H2v12h3"/>
        <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5"/>
        <path d="M14 17h1"/>
        <circle cx="7.5" cy="17.5" r="2.5"/>
        <circle cx="17.5" cy="17.5" r="2.5"/>
      `;
    case "runner":
      // Runner — human silhouette. Lucide `user` glyph so map pins clearly
      // read as a person (not a vehicle) at small sizes.
      return `
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      `;
    case "ride":
    default:
      // Car — sedan silhouette. Lucide `car` glyph.
      return `
        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
        <circle cx="7" cy="17" r="2"/>
        <path d="M9 17h6"/>
        <circle cx="17" cy="17" r="2"/>
      `;
  }
}
